import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { FollowUpQueueScope } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { FollowUpRow } from '../components/follow-ups/follow-up-row.js';
import {
  WorkspaceThemeSurface,
  WorkspaceThemeToggle,
} from '../components/layout/workspace-theme.js';
import { ListSkeleton, useToast, cn } from '@tailoring-crm/ui';
import { useFollowUpQueue } from '../lib/follow-ups/use-follow-ups.js';

/**
 * "What am I doing today."
 *
 * THIS PAGE EXISTS BECAUSE A TASK'S OWNER AND A CUSTOMER'S ASSIGNEE ARE ALLOWED TO DIFFER. The
 * customer list filters by assignee, so a rep handed three tasks on somebody else's customers
 * would see none of them anywhere — and those are exactly the tasks most likely to be dropped,
 * because somebody else was relying on them being done.
 *
 * Built to be worked DOWN rather than read: the number is a tap-to-call on every row, and the
 * tick completes the task in place, so clearing a morning's chasing never needs the detail page.
 * The customer's name links to their page for when it does.
 */

export function FollowUpsPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [scope, setScope] = useState<FollowUpQueueScope>('mine');
  const queue = useFollowUpQueue(scope);

  const items = queue.data?.items ?? [];

  return (
    <WorkspaceThemeSurface className="flex flex-col gap-6">
      <PageHeader
        title={t('followUps.title')}
        description={t('followUps.description')}
        className="workspace-atelier-header"
        action={
          // An owner reviewing the shop needs the whole board; a rep opens on their own work,
          // which is why `mine` is the default rather than the wider view.
          <>
            <WorkspaceThemeToggle />
            <div className="inline-flex items-center gap-1 rounded-full bg-muted p-1">
              {(['mine', 'all'] as const).map((one) => (
                <button
                  key={one}
                  type="button"
                  onClick={() => setScope(one)}
                  aria-pressed={scope === one}
                  className={cn(
                    'rounded-full px-3 py-1 text-sm font-medium transition-colors',
                    scope === one
                      ? 'bg-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(one === 'mine' ? 'followUps.scopeMine' : 'followUps.scopeAll')}
                </button>
              ))}
            </div>
          </>
        }
      />

      {/* The two numbers worth knowing before reading a single row. */}
      <div className="flex flex-wrap gap-3">
        <Tile label={t('followUps.overdue')} value={queue.data?.overdue ?? 0} tone="overdue" />
        <Tile label={t('followUps.dueToday')} value={queue.data?.dueToday ?? 0} tone="today" />
        <Tile label={t('followUps.open')} value={items.length} tone="none" />
      </div>

      {queue.isPending ? (
        <ListSkeleton rows={5} />
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          {t(scope === 'mine' ? 'followUps.empty' : 'followUps.emptyAll')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <FollowUpRow
              key={item.id}
              item={item}
              onDone={() => toast({ title: t('followUps.completed'), variant: 'success' })}
            />
          ))}
        </ul>
      )}
    </WorkspaceThemeSurface>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'overdue' | 'today' | 'none';
}): ReactElement {
  return (
    <div className="dossier-support-card min-w-28 rounded-lg border border-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p
        className={cn(
          'text-2xl font-semibold tabular-nums',
          tone === 'overdue' && value > 0 && 'text-destructive',
          tone === 'today' && value > 0 && 'text-amber-700 dark:text-amber-400',
        )}
      >
        {value}
      </p>
    </div>
  );
}
